import { FC } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@new/lib';

export interface WizardStepMeta {
  id: string;
  name: string;
  description: string;
}

type StepStatus = 'complete' | 'current' | 'upcoming';

interface WizardStepperProps {
  steps: WizardStepMeta[];
  currentIndex: number;
  /** Navigate to an already-completed step (upcoming steps are not selectable). */
  onStepClick?: (index: number) => void;
}

const statusFor = (index: number, current: number): StepStatus =>
  index < current ? 'complete' : index === current ? 'current' : 'upcoming';

export const WizardStepper: FC<WizardStepperProps> = ({ steps, currentIndex, onStepClick }) => (
  <nav aria-label="Progress" className="w-full">
    <ol className="flex flex-col overflow-hidden rounded-[9px] border border-solid border-[#CACDCE] bg-(--neutral-white-NW1000) lg:flex-row">
      {steps.map((step, index) => {
        const status = statusFor(index, currentIndex);
        const clickable = status === 'complete' && !!onStepClick;
        return (
          <li key={step.id} className="relative overflow-hidden lg:flex-1">
            <button
              type="button"
              disabled={!clickable}
              aria-current={status === 'current' ? 'step' : undefined}
              onClick={clickable ? () => onStepClick!(index) : undefined}
              className={cn(
                'group flex min-h-[88px] w-full items-center gap-3 px-4 py-4 text-left lg:px-5',
                clickable ? 'cursor-pointer' : 'cursor-default'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'absolute top-0 left-0 h-full w-1 lg:top-auto lg:bottom-0 lg:h-1 lg:w-full',
                  status === 'current' ? 'bg-(--primary-O900)' : 'bg-transparent'
                )}
              />
              <span className="shrink-0">
                <span
                  className={cn(
                    'flex size-9 items-center justify-center rounded-full text-sm font-semibold',
                    status === 'complete'
                      ? 'bg-(--primary-O900)'
                      : status === 'current'
                        ? 'border-2 border-(--primary-O900)'
                        : 'border-2 border-(--neutral-black-NB200)'
                  )}
                >
                  {status === 'complete' ? (
                    <Check aria-hidden="true" className="size-5 text-white" />
                  ) : (
                    <span
                      className={cn(
                        status === 'current'
                          ? 'text-(--primary-O900)'
                          : 'text-(--neutral-black-NB700)'
                      )}
                    >
                      {step.id}
                    </span>
                  )}
                </span>
              </span>
              <span className="flex min-w-0 flex-col">
                <span
                  className={cn(
                    'text-sm font-semibold leading-tight',
                    status === 'current' ? 'text-(--primary-O900)' : 'text-(--neutral-black-NB1000)'
                  )}
                >
                  {step.name}
                </span>
                <span className="text-xs leading-tight text-(--neutral-black-NB700)">
                  {step.description}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);
